import { ref, onMounted, computed, watch } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import Header from '@/components/HeaderDashboard.vue';
import Swal from 'sweetalert2';
import { hasPermission } from '@/store/userStore';
import { DesplegableService } from '@/services/DesplegableService';
import { ValoresService } from '@/services/ValoresService';
import { UnidadMedidaService } from '@/services/UnidadMedidaService';
import { ClasificacionMarcaService } from '@/services/ClasificacionMarcaService';
import { PlusIcon, EditIcon, TrashIcon, ListIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, TagIcon, TypeIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, HashIcon, ToggleLeftIcon, CalendarIcon, ArrowLeftRightIcon, ChevronDownIcon, PowerIcon, InfoIcon, LoaderIcon } from 'lucide-vue-next';
const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
});
import AlphabetFilter from '@/components/AlphabetFilter.vue';
import draggable from 'vuedraggable';
export default {
    name: 'Desplegables',
    components: {
        Sidebar, Header,
        PlusIcon, EditIcon, TrashIcon, ListIcon, SearchIcon,
        ChevronLeftIcon, ChevronRightIcon, XIcon,
        TagIcon, TypeIcon, DownloadIcon, FileTextIcon, GripVerticalIcon, draggable,
        HashIcon, ToggleLeftIcon, CalendarIcon, ArrowLeftRightIcon, ChevronDownIcon, InfoIcon, PowerIcon, LoaderIcon,
        AlphabetFilter
    },
    setup() {
        // --- ESTADO GENERAL ---
        const desplegables = ref([]);
        const isLoading = ref(false);
        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        // --- FILTROS Y PAGINACIÓN ---
        const searchQuery = ref('');
        const selectedLetter = ref('');
        const currentPage = ref(1);
        const itemsPerPage = ref(8);
        // --- TIPOS DISPONIBLES ---
        const tiposAtributo = [
            { value: 'desplegable', label: 'Desplegable', icon: ChevronDownIcon },
            { value: 'texto_libre', label: 'Texto libre', icon: TypeIcon },
            { value: 'numerico', label: 'Numérico', icon: HashIcon },
            { value: 'booleano', label: 'Sí / No', icon: ToggleLeftIcon },
            { value: 'fecha', label: 'Fecha', icon: CalendarIcon },
            { value: 'rango', label: 'Rango', icon: ArrowLeftRightIcon },
        ];
        const tipoLabel = (tipo) => tiposAtributo.find(t => t.value === tipo)?.label ?? tipo;
        // --- UNIDADES DE MEDIDA ---
        const unidades = ref([]);
        const unidadesPorCategoria = computed(() => {
            const map = {};
            unidades.value.forEach(u => {
                if (!map[u.categoria])
                    map[u.categoria] = [];
                map[u.categoria].push(u);
            });
            return Object.entries(map).map(([categoria, us]) => ({ categoria, unidades: us }));
        });
        const loadUnidades = async () => {
            try {
                unidades.value = await UnidadMedidaService.getAll();
            }
            catch { /* silencioso */ }
        };
        const clasis = ref([]);
        const loadClasificaciones = async () => {
            try {
                clasis.value = await ClasificacionMarcaService.getAll();
            }
            catch { /* silencioso */ }
        };
        // --- MODAL PRINCIPAL (Atributo) ---
        const isModalOpen = ref(false);
        const isEditing = ref(false);
        const currentForm = ref({ nombreAtributoID: 0, nombreAtributo: '', tipoAtributo: 'desplegable', unidadID: null, decimales: 0 });
        // --- MODAL VALORES ---
        const isModalOpenValores = ref(false);
        const isEditingValor = ref(false);
        const currentValor = ref(null); // El atributo padre actual
        const valores = ref([]);
        const searchValorQuery = ref('');
        const filterClasificacionId = ref(null);
        const isSavingValor = ref(false);
        const valorForm = ref({ valorID: 0, valor: '', nombreAtributoID: 0, orden: 0, clasificacionMarcaId: null, clasificacionMarcaNombre: '' });
        const filteredValores = computed(() => {
            let list = [...valores.value];
            if (searchValorQuery.value) {
                list = list.filter(v => v.valor.toLowerCase().includes(searchValorQuery.value.toLowerCase()));
            }
            if (isMarcaDropdown.value && filterClasificacionId.value !== null) {
                if (filterClasificacionId.value === 0) {
                    list = list.filter(v => !v.clasificacionMarcaId);
                }
                else {
                    list = list.filter(v => v.clasificacionMarcaId === filterClasificacionId.value);
                }
            }
            return list;
        });
        const isMarcaDropdown = computed(() => {
            return currentValor.value?.nombreAtributo && currentValor.value.nombreAtributo.toLowerCase().includes('marca');
        });
        // Export Excel State
        const isExportModalOpen = ref(false);
        const isExporting = ref(false);
        const availableColumns = ref([
            { id: 1, name: "Atributo" },
            { id: 2, name: "Valor" }
        ]);
        const exportConfig = ref({
            format: 'excel',
            selectedColumns: ["Atributo", "Valor"],
            sortColumn: "Atributo",
            sortDirection: "asc"
        });
        // ==========================================
        // LÓGICA DE DATOS
        // ==========================================
        const loadDesplegables = async () => {
            isLoading.value = true;
            try {
                const res = await DesplegableService.getAllDesplegables();
                desplegables.value = res || [];
            }
            catch (e) {
                console.error(e);
            }
            finally {
                isLoading.value = false;
            }
        };
        // --- COMPUTADOS PARA LA TABLA ---
        const processedDesplegables = computed(() => {
            let list = [...desplegables.value];
            if (searchQuery.value) {
                list = list.filter(d => d.nombreAtributo.toLowerCase().includes(searchQuery.value.toLowerCase()));
            }
            if (selectedLetter.value) {
                list = list.filter(d => d.nombreAtributo.toUpperCase().startsWith(selectedLetter.value));
            }
            return list.sort((a, b) => a.nombreAtributo.localeCompare(b.nombreAtributo));
        });
        const paginatedDesplegables = computed(() => {
            const start = (currentPage.value - 1) * itemsPerPage.value;
            return processedDesplegables.value.slice(start, start + itemsPerPage.value);
        });
        const totalPages = computed(() => Math.ceil(processedDesplegables.value.length / itemsPerPage.value) || 1);
        const totalDesplegablesCount = computed(() => desplegables.value.length);
        // Watchers para resetear paginación
        watch([searchQuery, selectedLetter], () => currentPage.value = 1);
        // ==========================================
        // LÓGICA MODAL PRINCIPAL
        // ==========================================
        const openModal = () => {
            isEditing.value = false;
            currentForm.value = { nombreAtributoID: 0, nombreAtributo: '', tipoAtributo: 'desplegable', unidadID: null, decimales: 0 };
            isModalOpen.value = true;
        };
        const editDesplegable = (item) => {
            isEditing.value = true;
            currentForm.value = {
                nombreAtributoID: item.nombreAtributoID,
                nombreAtributo: item.nombreAtributo,
                tipoAtributo: item.tipoAtributo || 'desplegable',
                unidadID: item.unidadID ?? null,
                decimales: item.unidad?.decimales ?? 0,
            };
            isModalOpen.value = true;
        };
        const closeModal = () => isModalOpen.value = false;
        const saveDesplegable = async () => {
            try {
                const payload = {
                    nombreAtributoID: currentForm.value.nombreAtributoID,
                    nombreAtributo: currentForm.value.nombreAtributo,
                    tipoAtributo: currentForm.value.tipoAtributo,
                    unidadID: (currentForm.value.tipoAtributo === 'numerico' || currentForm.value.tipoAtributo === 'rango')
                        ? currentForm.value.unidadID
                        : null,
                    decimales: currentForm.value.decimales,
                };
                if (isEditing.value) {
                    await DesplegableService.updateDesplegable(payload.nombreAtributoID, payload);
                    Toast.fire({ icon: 'success', title: 'Atributo actualizado correctamente' });
                }
                else {
                    await DesplegableService.addDesplegable(payload);
                    Toast.fire({ icon: 'success', title: 'Nuevo atributo creado' });
                }
                closeModal();
                loadDesplegables();
            }
            catch (e) {
                Swal.fire('Error', e?.message || 'No se pudo guardar', 'error');
            }
        };
        const confirmDeleteDesplegable = async (id) => {
            const res = await Swal.fire({ title: '¿Eliminar?', text: 'Esto borrará también sus valores.', icon: 'warning', showCancelButton: true });
            if (res.isConfirmed) {
                try {
                    await DesplegableService.deleteDesplegable(id);
                    loadDesplegables();
                }
                catch (e) {
                    Swal.fire({
                        title: 'No se puede eliminar',
                        text: e?.message || 'No se pudo eliminar el desplegable.',
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                }
            }
        };
        // ==========================================
        // LÓGICA MODAL VALORES
        // ==========================================
        const openValoresModal = async (item) => {
            currentValor.value = item;
            valorForm.value = { valorID: 0, valor: '', nombreAtributoID: item.nombreAtributoID, orden: 0, clasificacionMarcaId: null, clasificacionMarcaNombre: '' };
            isEditingValor.value = false;
            searchValorQuery.value = '';
            filterClasificacionId.value = null;
            // Cargar valores
            const res = await ValoresService.getValoresByNombreAtributoId(item.nombreAtributoID);
            valores.value = (res || []).sort((a, b) => a.valor.localeCompare(b.valor));
            isModalOpenValores.value = true;
        };
        const closeValoresModal = () => isModalOpenValores.value = false;
        const handleSubmitValor = async () => {
            if (!valorForm.value.valor.trim() || isSavingValor.value)
                return;
            isSavingValor.value = true;
            try {
                if (isEditingValor.value) {
                    await ValoresService.updateValor(valorForm.value.valorID, valorForm.value);
                }
                else {
                    await ValoresService.addValor({ ...valorForm.value, orden: valores.value.length + 1 });
                }
                // Recargar lista
                const res = await ValoresService.getValoresByNombreAtributoId(currentValor.value.nombreAtributoID);
                valores.value = (res || []).sort((a, b) => a.valor.localeCompare(b.valor));
                // Reset form input
                valorForm.value.valor = '';
                valorForm.value.valorID = 0;
                valorForm.value.clasificacionMarcaId = null;
                isEditingValor.value = false;
            }
            catch (e) {
                console.error(e);
            }
            finally {
                isSavingValor.value = false;
            }
        };
        const startEditValor = (item) => {
            valorForm.value = { ...item };
            isEditingValor.value = true;
        };
        const cancelEditValor = () => {
            valorForm.value.valor = '';
            valorForm.value.valorID = 0;
            valorForm.value.clasificacionMarcaId = null;
            isEditingValor.value = false;
        };
        const confirmDeleteValor = async (id) => {
            const res = await Swal.fire({ title: '¿Eliminar valor?', icon: 'warning', showCancelButton: true });
            if (res.isConfirmed) {
                try {
                    await ValoresService.deleteValor(id);
                    // Recargar
                    const updateRes = await ValoresService.getValoresByNombreAtributoId(currentValor.value.nombreAtributoID);
                    valores.value = updateRes || [];
                }
                catch (e) {
                    Swal.fire({
                        title: 'No se puede eliminar',
                        text: e?.message || 'No se pudo eliminar el valor.',
                        icon: 'error',
                        confirmButtonText: 'Entendido'
                    });
                }
            }
        };
        // Export functions
        function openExportModal() {
            isExportModalOpen.value = true;
        }
        function closeExportModal() {
            isExportModalOpen.value = false;
        }
        async function handleExport() {
            if (exportConfig.value.selectedColumns.length === 0) {
                Swal.fire({ title: 'Atención', text: 'Debe seleccionar al menos una columna.', icon: 'warning' });
                return;
            }
            isExporting.value = true;
            try {
                await ValoresService.exportReport({
                    searchTerm: searchQuery.value,
                    letter: selectedLetter.value || "",
                    selectedColumns: availableColumns.value
                        .map(c => c.name)
                        .filter(name => exportConfig.value.selectedColumns.includes(name)),
                    format: exportConfig.value.format,
                    sortColumn: exportConfig.value.sortColumn,
                    sortDirection: exportConfig.value.sortDirection
                });
                closeExportModal();
                Toast.fire({ title: '¡Éxito!', text: 'Reporte generado correctamente.', icon: 'success' });
            }
            catch (error) {
                Swal.fire({ title: 'Error', text: 'No se pudo generar el reporte.', icon: 'error' });
            }
            finally {
                isExporting.value = false;
            }
        }
        // --- UTILIDADES ---
        const resetFilter = () => { selectedLetter.value = ''; searchQuery.value = ''; };
        const prevPage = () => { if (currentPage.value > 1)
            currentPage.value--; };
        const nextPage = () => { if (currentPage.value < totalPages.value)
            currentPage.value++; };
        const toggleActive = async (item) => {
            try {
                await DesplegableService.toggleActivo(item.nombreAtributoID);
                item.active = !item.active;
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: `Estado ${item.active ? 'activado' : 'desactivado'}`,
                    showConfirmButton: false,
                    timer: 2000
                });
            }
            catch (e) {
                Swal.fire('Error', e?.message || 'No se pudo cambiar el estado', 'error');
            }
        };
        const toggleActiveValor = async (item) => {
            try {
                await ValoresService.toggleActivo(item.valorID);
                item.active = item.active === false ? true : false;
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: `Valor ${item.active ? 'activado' : 'desactivado'}`,
                    showConfirmButton: false,
                    timer: 2000
                });
            }
            catch (e) {
                Swal.fire('Error', e?.message || 'No se pudo cambiar el estado del valor', 'error');
            }
        };
        onMounted(() => { loadDesplegables(); loadUnidades(); loadClasificaciones(); });
        return {
            hasPermission,
            // Data
            desplegables, isLoading, paginatedDesplegables,
            searchQuery, selectedLetter, currentPage, totalPages, totalDesplegablesCount,
            // Modal Main
            isModalOpen, isEditing, currentForm,
            // Tipos y unidades
            tiposAtributo, tipoLabel, unidades, unidadesPorCategoria,
            // Modal Valores
            isModalOpenValores, isEditingValor, currentValor, valores, valorForm, isMarcaDropdown, searchValorQuery, filterClasificacionId, filteredValores, isSavingValor,
            // Methods
            resetFilter, prevPage, nextPage,
            openModal, closeModal, saveDesplegable, confirmDeleteDesplegable, editDesplegable,
            openValoresModal, closeValoresModal, handleSubmitValor, startEditValor, cancelEditValor, confirmDeleteValor,
            isExportModalOpen, isExporting, availableColumns, exportConfig, openExportModal, closeExportModal, handleExport,
            toggleActive, toggleActiveValor, clasis
        };
    }
};
